import { useState, useEffect } from 'react';

/**
 * Custom hook for using Intersection Observer API
 *
 * @param {React.RefObject} targetRef - Ref to the target element to observe
 * @param {IntersectionObserverInit} options - Options for the IntersectionObserver
 * @returns {boolean} isIntersecting - Whether the target element is currently intersecting
 */
export function useIntersectionObserver(targetRef, options = {}) {
  const [isIntersecting, setIsIntersecting] = useState(false);
  useEffect(() => {
    if (!targetRef.current || !options) {
      // nothing to observe yet
      return;
    }

    const target = targetRef?.current;

    // Early return if target doesn't exist
    if (!target) {
      setIsIntersecting(false);
      return;
    }

    // Create intersection observer
    const observer = new IntersectionObserver((entries) => {
      // There should only be one entry since we're observing one element
      const entry = entries[0];
      if (entry) {
        setIsIntersecting(entry.isIntersecting);
      }
    }, options);

    // Start observing the target
    observer.observe(target);

    // Cleanup function
    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRef.current, options?.root, options?.rootMargin, options?.threshold]);

  return isIntersecting;
}

export default useIntersectionObserver;
